// @ts-ignore
import Camera from '../../Camera';
import Time from '../../Utils/Time';
import { autoInjectable, singleton } from 'tsyringe';
import Renderer from '../../Components/Renderer';
import VrScene from '../../Components/Three/VrScene';
import AxiosHttpClient from '../../Network/AxiosHttpClient';
import {
    Color,
    DoubleSide,
    MathUtils,
    Mesh,
    MeshBasicMaterial,
    MeshPhongMaterial,
    Object3D,
    PlaneGeometry,
    PointLight,
    Vector3,
    VideoTexture,
    SRGBColorSpace,
} from 'three';
import Resources from '../../Resources';
import IntersectionContainer from '../../Components/Controllers/IntersectionContainer';
import ColyseusClient from '../../Network/ColyseusClient';
import SchoolLobby from '../SchoolLobby/SchoolLobby';
import Utils from '../../Utils/Utils';
import { DashboardNewsClass } from '../../Components/Ui/DashboardNewsClass';
import Model3dViewer from '../../Components/Model3dViewer';
import SkyboxChanger from '../../Components/SkyboxChanger';
import {
    ERASER_LINE_WIDTH,
    ICtxPath,
    RESET_CANVAS_LINE_WIDTH,
    REST_CANVAS,
    SketchBoard,
} from '../../Components/Ui/SketchBoard';
import { AXIS, ZoomToView } from '../../Components/ZoomTo/ZoomTo.view';
import TextGenerator from '../../Components/Text/TextGenerator';
import { IImage } from '../../react/sketchboards/types';
import TranslationService from '../../Translations/TranslationService';
//@ts-ignore
import robot from '../../../../static/Roboto/Roboto-Regular.ttf';
import { Text } from 'troika-three-text';
import DoorService from '../../Components/Doors/Door.service';

declare global {
    interface Window {
        sketchboard1: any;
        sketchboard2: any;
        sketchboard3: any;
    }
}

@singleton()
@autoInjectable()
export default class Classroom extends VrScene {
    public name = 'classroom';

    public worldModel: Object3D;
    public sketchBoardModel1: Object3D;
    public sketchBoardModel2: Object3D;
    public sketchBoardModel3: Object3D;

    public projector: Mesh;

    public videoElement: HTMLVideoElement;

    public sketchBoard1: SketchBoard;
    public sketchBoard2: SketchBoard;
    public sketchBoard3: SketchBoard;

    public skyboxChanger: SkyboxChanger;
    public screenSharing = true;

    public constructor(
        public camera?: Camera,
        public time?: Time,
        public renderer?: Renderer,
        public httpClient?: AxiosHttpClient,
        public resources?: Resources,
        public intersectionContainer?: IntersectionContainer,
        public colyseusClient?: ColyseusClient,
        public modelViewer?: Model3dViewer,
        public doorService?: DoorService,
    ) {
        super();

        this.setWorldModel();

        this.sketchBoardModel1 = this.resources.items.sketchBoard.scene.clone();
        this.sketchBoardModel1.traverse((child) => {
            child.frustumCulled = false;
        });
        this.sketchBoardModel1.position.set(-5, -0.6, -10);
        this.sketchBoardModel1.rotateY(MathUtils.DEG2RAD * 270);
        this.sketchBoardModel1.scale.set(0.5, 0.5, 0.5);
        this.add(this.sketchBoardModel1);

        this.sketchBoardModel2 = this.resources.items.sketchBoard.scene.clone();
        this.sketchBoardModel2.traverse((child) => {
            child.frustumCulled = false;
        });
        this.sketchBoardModel2.position.set(0, -0.6, -10);
        this.sketchBoardModel2.rotateY(MathUtils.DEG2RAD * 270);
        this.sketchBoardModel2.scale.set(0.5, 0.5, 0.5);
        this.add(this.sketchBoardModel2);

        this.sketchBoardModel3 = this.resources.items.sketchBoard.scene.clone();
        this.sketchBoardModel3.traverse((child) => {
            child.frustumCulled = false;
        });
        this.sketchBoardModel3.position.set(-2, -0.6, 10);
        this.sketchBoardModel3.rotateY(MathUtils.DEG2RAD * 90);
        this.sketchBoardModel3.scale.set(0.5, 0.5, 0.5);
        this.add(this.sketchBoardModel3);

        this.worldModel.getObjectByName('Text017').visible = false;
        this.worldModel.getObjectByName('Text008').visible = false;
        this.worldModel.getObjectByName('Text020').visible = false;
        this.setLabel();
    }

    public setLabel() {
        const schoolLobbyLabel = new Text();

        schoolLobbyLabel.text = TranslationService.translate('vr.menu_school');
        schoolLobbyLabel.fontSize = 0.2;
        schoolLobbyLabel.color = 0xffff00;
        schoolLobbyLabel.anchorX = 'left';
        schoolLobbyLabel.anchorY = 'middle';
        schoolLobbyLabel.depthOffset = 0.1;
        schoolLobbyLabel.material = new MeshPhongMaterial({
            color: new Color(0xffff00),
            emissive: new Color(0x444400),
            side: DoubleSide,
        });
        schoolLobbyLabel.position.set(10, 0.2, -0.8);

        schoolLobbyLabel.font = robot;

        schoolLobbyLabel.rotation.y = -Math.PI / 2;

        this.add(schoolLobbyLabel);

        const screenSharingLabel = new Text();

        screenSharingLabel.text = TranslationService.translate('vr.screen_sharing');
        screenSharingLabel.fontSize = 0.2;
        screenSharingLabel.color = 0x772385;
        screenSharingLabel.anchorX = 'left';
        screenSharingLabel.anchorY = 'middle';
        screenSharingLabel.depthOffset = 0.1;
        screenSharingLabel.material = new MeshPhongMaterial({
            color: new Color(0x772385),
            emissive: new Color(0x772385),
            side: DoubleSide,
        });
        screenSharingLabel.position.set(-10, 4, 4.6);

        screenSharingLabel.font = robot;

        screenSharingLabel.rotation.y = Math.PI / 2;

        this.add(schoolLobbyLabel);
        this.add(screenSharingLabel);
    }

    public setWorldModel() {
        this.worldModel = this.resources.items.classScene.scene.clone();
        this.worldModel.traverse((child) => {
            child.frustumCulled = false;
        });
        this.worldModel.position.y = -2.5;
        this.add(this.worldModel);

        const geometry = new PlaneGeometry(8, 4.5, 1);
        const material = new MeshBasicMaterial({
            color: 0xffffff,
            side: DoubleSide,
        });
        this.projector = new Mesh(geometry, material);
        this.projector.name = 'videoProjector';

        this.projector.name = 'projector';

        this.worldModel.getObjectByName('Plane055').getWorldPosition(this.projector.position);
        this.projector.translateX(0.1);
        this.projector.rotateY(Math.PI / 2);
        // this.projector.visible = false;

        this.add(this.projector);

        const floorGroup = new Object3D();

        floorGroup.name = 'floorGroup';

        // const direction = new PointLight(0xffffff, 0.5);
        // direction.position.y = 5;
        // this.add(new AmbientLight(new Color('white'), 0.5))
        // this.add(direction);

        const mesh = new Mesh(new PlaneGeometry(20, 20, 32), new MeshPhongMaterial({ color: new Color('pink') }));

        mesh.name = 'floor';
        mesh.visible = false;
        mesh.position.y = -2.5;
        mesh.quaternion.setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2);

        this.addBoundingBox(mesh, false);

        floorGroup.add(mesh);

        this.add(floorGroup);
    }

    public drawImagesToSketchBoards(sketchBoardName: string, images: IImage) {
        switch (sketchBoardName) {
            case 'sketchboard':
                this.drawImage(this.sketchBoard1, images);
                break;
            case 'sketchboard2':
                this.drawImage(this.sketchBoard2, images);
                break;
            case 'sketchboard3':
                this.drawImage(this.sketchBoard3, images);
        }
    }

    public drawPathToSketchBoards(sketchBoardName: string, ctxPaths: ICtxPath) {
        switch (sketchBoardName) {
            case 'sketchboard':
                this.drawLineOnSketchboard(this.sketchBoard1, ctxPaths);
                break;
            case 'sketchboard2':
                this.drawLineOnSketchboard(this.sketchBoard2, ctxPaths);
                break;
            case 'sketchboard3':
                this.drawLineOnSketchboard(this.sketchBoard3, ctxPaths);
        }
    }

    public drawDrawablesToSketchBoards(sketchboardName: string, allDrawables: any[]) {
        switch (sketchboardName) {
            case 'sketchboard':
                this.drawAllDrawablesOnSketchboard(this.sketchBoard1, allDrawables);
                break;
            case 'sketchboard2':
                this.drawAllDrawablesOnSketchboard(this.sketchBoard2, allDrawables);
                break;
            case 'sketchboard3':
                this.drawAllDrawablesOnSketchboard(this.sketchBoard3, allDrawables);
        }
    }
    public async drawAllDrawablesOnSketchboard(sketchboard: SketchBoard, drawables: any[]) {
        for (const drawable of drawables) {
            if (drawable.type === 'image') {
                await this.drawImage(sketchboard, drawable.data);
            } else {
                this.drawLineOnSketchboard(sketchboard, drawable.data);
            }
        }
    }

    public drawLineOnSketchboard(sketchBoard: SketchBoard, ctxPathsToDraw: ICtxPath) {
        if (ctxPathsToDraw.lineWidth === ERASER_LINE_WIDTH) {
            sketchBoard.setDrawingMode('erase');
        } else {
            sketchBoard.setDrawingMode('drawing');
        }

        if (ctxPathsToDraw.lineWidth === RESET_CANVAS_LINE_WIDTH) {
            sketchBoard.canvasCtx.clearRect(0, 0, sketchBoard.canvas.width, sketchBoard.canvas.height);
            // this.canvasCtx.fillStyle = '#ffffff';
            sketchBoard.canvasCtx.fillStyle = 'rgba(255, 255, 255, 0)';
            sketchBoard.canvasCtx.fillRect(0, 0, sketchBoard.canvas.width, sketchBoard.canvas.height);
        }

        sketchBoard.drawingLineOnCanvas(
            ctxPathsToDraw.x1,
            ctxPathsToDraw.y1,
            ctxPathsToDraw.x2,
            ctxPathsToDraw.y2,
            ctxPathsToDraw.color,
        );
    }

    public async drawImage(sketchboard: SketchBoard, image: IImage) {
        await sketchboard.drawImage(image);
    }

    public onChange(changes: any[]): void {
        changes.forEach((element) => {
            if (element.previousValue === element.value) {
                return;
            }
            switch (element.field) {
                case 'skybox':
                    this.skyboxChanger.setSkyboxFromName(element.value);
                    break;
            }
        });
    }

    public onLeave() {
        this.onUpdate = null;
        // this.time.remove(this.onUpdate);
    }

    public async start() {
        this.setTpRings(20, 20, new Vector3(-13, -2.42, -13));

        const roomLabel = new TextGenerator(this.roomName.replaceAll(/[^\S ]/g, ''));
        roomLabel.setAnchor('RIGHT');
        roomLabel.position.set(10, -2.4, 9.5);
        roomLabel.rotateY(-MathUtils.DEG2RAD * 90);
        this.add(roomLabel);

        const zoomToProjector = new ZoomToView({
            target: this.projector.position,
            axis: AXIS.X,
            offset: 5,
        });

        // this.modelViewer.position.set(7.326, 0, -6.5);
        this.modelViewer.position.set(0, -1.8, 0);
        // this.modelViewer.scale.set(2, 2, 2);
        this.add(this.modelViewer);

        zoomToProjector.position.copy(this.projector.position);

        zoomToProjector.rotateY(MathUtils.DEG2RAD * 90);
        zoomToProjector.translateX(5);
        zoomToProjector.translateY(2.5);

        this.add(zoomToProjector);

        this.skyboxChanger = new SkyboxChanger(this);

        this.skyboxChanger.rotateY(Math.PI / 2);
        this.skyboxChanger.position.set(-9, -2, 0);

        this.add(this.skyboxChanger);

        Utils.waitForElm(`[data-user-type="teacher"]`).then((elem: HTMLVideoElement) => {
            this.videoElement = elem;
            const videoTexture = new VideoTexture(elem);
            videoTexture.colorSpace = SRGBColorSpace;

            if (!this.videoElement.classList.contains('transmiting')) {
                //@ts-ignore
                this.projector.material = new MeshBasicMaterial({
                    map: videoTexture,
                });
                // this.projector.material.opacity = 0;

                //@ts-ignore
                this.projector.material.needsUpdate = true;
            }
        });

        Utils.waitForElm(`#video-local`).then((elem: HTMLVideoElement) => {
            this.videoElement = elem;

            const videoTexture = new VideoTexture(elem);
            videoTexture.colorSpace = SRGBColorSpace;

            //@ts-ignore
            this.projector.material = new MeshBasicMaterial({
                map: videoTexture,
            });
            // this.projector.material.opacity = 0;

            //@ts-ignore
            this.projector.material.needsUpdate = true;
        });

        const classNewsBoard = new DashboardNewsClass();
        this.worldModel.getObjectByName('Plane057').getWorldPosition(classNewsBoard.position);
        classNewsBoard.translateZ(-0.01);

        classNewsBoard.rotateY(MathUtils.DEG2RAD * 180);
        // classNewsBoard.position.set(15, 1.4, 2)
        classNewsBoard.scale.set(1, 1, 1);
        this.add(classNewsBoard);

        const zoomToSchoolDashboard = new ZoomToView({
            target: classNewsBoard.position,
            axis: AXIS.Z,
            offset: -2.5,
        });

        zoomToSchoolDashboard.translateX(2.6);
        zoomToSchoolDashboard.translateY(1.2);

        classNewsBoard.add(zoomToSchoolDashboard);

        this.sketchBoard1 = new SketchBoard();
        window.sketchboard1 = this.sketchBoard1;
        this.sketchBoard1.addEventListener('updateSketchBoard' as any, (e: any) => {
            this.colyseusClient.updateSketchBoard(e.data);
        });

        //---############################# TUUU
        this.sketchBoardModel1.getObjectByName('blank_not004').getWorldPosition(this.sketchBoard1.position);

        const zoomToSketchboard1 = new ZoomToView({
            target: this.sketchBoard1.position,
            axis: AXIS.Z,
            offset: 2.5,
        });

        zoomToSketchboard1.translateX(2.2);
        zoomToSketchboard1.translateY(1.1);

        this.sketchBoard1.add(zoomToSketchboard1);
        this.sketchBoard1.addEventListener('resetSketchBoard' as any, () => {
            this.colyseusClient.updateSketchBoard([REST_CANVAS]);
        });

        // this.sketchBoard1.position.set(-5, -1.2, -10);
        this.sketchBoard1.position.set(-5, -0.6, -10);
        this.add(this.sketchBoard1);

        this.sketchBoard2 = new SketchBoard();
        window.sketchboard2 = this.sketchBoard2;
        this.sketchBoard2.addEventListener('updateSketchBoard' as any, (e: any) => {
            this.colyseusClient.updateSketchBoard2(e.data);
        });

        const zoomToSketchboard2 = new ZoomToView({
            target: this.sketchBoard2.position,
            axis: AXIS.Z,
            offset: 2.5,
        });

        zoomToSketchboard2.translateX(2.2);
        zoomToSketchboard2.translateY(1.1);

        this.sketchBoard2.add(zoomToSketchboard2);

        this.sketchBoard2.addEventListener('resetSketchBoard' as any, () => {
            this.colyseusClient.updateSketchBoard2([REST_CANVAS]);
        });

        this.sketchBoard2.position.set(0, -0.6, -10);

        this.add(this.sketchBoard2);

        this.sketchBoard3 = new SketchBoard();
        window.sketchboard3 = this.sketchBoard3;

        this.sketchBoard3.addEventListener('updateSketchBoard' as any, (e: any) => {
            this.colyseusClient.updateSketchBoard3(e.data);
        });

        this.sketchBoard3.addEventListener('resetSketchBoard' as any, () => {
            this.colyseusClient.updateSketchBoard3([REST_CANVAS]);
        });

        this.sketchBoard3.position.set(-2, -0.6, 10);
        this.sketchBoard3.rotateY(MathUtils.DEG2RAD * 180);
        this.add(this.sketchBoard3);

        const zoomToSketchboard3 = new ZoomToView({
            target: this.sketchBoard3.position,
            axis: AXIS.Z,
            offset: -2.5,
        });

        zoomToSketchboard3.translateX(2.2);
        zoomToSketchboard3.translateY(1.1);

        this.sketchBoard3.add(zoomToSketchboard3);

        const schoolLobbyDoor = this.doorService.setDoorTo(
            new SchoolLobby(),
            this.resources.items.user.school_id,
            true,
            'Lobby',
        );
        schoolLobbyDoor.rotateY(-(MathUtils.DEG2RAD * 90));
        this.worldModel.getObjectByName('Cube027').getWorldPosition(schoolLobbyDoor.position);
        this.add(schoolLobbyDoor);
    }

    public onJoin(state: any): void {}

    public update() {}

    public destroy() {
        this.modelViewer.cleanup();
        super.destroy();
    }
}
